import { isClosed } from './presentation';
import { cityText } from './property-feed';
import { documentTime } from './record-dates';
import type { RecordSummary } from './types';

const DAY = 86_400_000;
/** An approved permit with no work window is history once it is this old. */
const STALE_OPEN_DAYS = 365;

/**
 * Where a Public Works permit sits relative to the parcel. The city publishes
 * street permits by block (CNN), so only `address` means the permit itself
 * names one of this property's addresses; `neighbor` names another address on
 * the block and `block` names only the street segment.
 */
export type PublicWorksPlacement = 'address' | 'neighbor' | 'block';

/**
 * Lifecycle derived from the city status and the permit window, relative to
 * `now`. `upcoming`/`active` are the states worth a reader's attention;
 * `ended`/`closed` are history and render compactly.
 */
export type PublicWorksState = 'upcoming' | 'active' | 'open' | 'ended' | 'closed';

export type PublicWorksTypeKey =
  | 'excavation'
  | 'emergency'
  | 'tempOccupancy'
  | 'conformity'
  | 'storageContainer'
  | 'minorEncroachment'
  | 'streetImprovement'
  | 'sewerRepair'
  | 'sidewalkRepair'
  | 'wireless'
  | 'parklet'
  | 'tablesChairs'
  | 'display'
  | 'banners'
  | 'nightNoise'
  | 'foodFacility'
  | 'debrisBox'
  | 'streetSpace'
  | 'boring'
  | 'tankRemoval'
  | 'other';

/**
 * Permits that occupy the street rather than dig it up: their dates are a
 * permit period ("parklet allowed through …"), not a construction window.
 */
const OCCUPANCY_TYPES = new Set<PublicWorksTypeKey>([
  'tempOccupancy',
  'storageContainer',
  'parklet',
  'tablesChairs',
  'display',
  'banners',
  'nightNoise',
  'foodFacility',
  'debrisBox',
  'streetSpace',
]);

/** Longer than this and the city text is a description, not a title. */
const HEADLINE_MAX = 90;

const TYPE_BY_PREFIX: Array<[RegExp, PublicWorksTypeKey]> = [
  [/^\d{2}ECN-/i, 'emergency'],
  [/^\d{2}(?:EXC|E|PGE|TE)-/i, 'excavation'],
  [/^\d{2}TOC-/i, 'tempOccupancy'],
  [/^\d{2}CN-/i, 'conformity'],
  [/^\d{2}SC-/i, 'storageContainer'],
  [/^\d{2}MSE-/i, 'minorEncroachment'],
  [/^\d{2}IE-/i, 'streetImprovement'],
  [/^\d{2}SWR-/i, 'sewerRepair'],
  [/^\d{2}SW-/i, 'sidewalkRepair'],
  [/^\d{2}WRM-/i, 'wireless'],
  [/^\d{2}PKT-/i, 'parklet'],
  [/^\d{2}DB-/i, 'debrisBox'],
];

const TYPE_BY_NAME: Array<[RegExp, PublicWorksTypeKey]> = [
  [/emergency/i, 'emergency'],
  [/excav/i, 'excavation'],
  [/temp\s*occup|occupancy/i, 'tempOccupancy'],
  [/conform/i, 'conformity'],
  [/stor.*cont/i, 'storageContainer'],
  [/encroach/i, 'minorEncroachment'],
  [/improvement/i, 'streetImprovement'],
  [/sewer/i, 'sewerRepair'],
  [/sidewalk/i, 'sidewalkRepair'],
  [/wireless|antenna/i, 'wireless'],
  [/parklet|shared\s*spaces/i, 'parklet'],
  [/table.*chair/i, 'tablesChairs'],
  [/display/i, 'display'],
  [/banner/i, 'banners'],
  [/night\s*noise/i, 'nightNoise'],
  [/food/i, 'foodFacility'],
  [/debris/i, 'debrisBox'],
  [/street\s*space|addl\s*st\s*spac/i, 'streetSpace'],
  [/boring/i, 'boring'],
  [/tank/i, 'tankRemoval'],
];

function field(record: RecordSummary, label: string) {
  return (record.pages?.[0]?.fields?.[label] ?? '').trim();
}

function titleCaseStreet(value: string) {
  return value
    .split(/\s+/)
    .map((token) => {
      if (/^\d/.test(token)) return token.toLowerCase();
      if (/^(st|ave|blvd|dr|rd|ln|ct|pl|way|hwy|ter|cir)\.?$/i.test(token))
        return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(' ');
}

/** "10TH AVE · ANZA ST to BALBOA ST" → { street: "10th Ave", from, to }. */
export function publicWorksStreetBlock(value: string) {
  const [street = '', range = ''] = value.split('·').map((part) => part.trim());
  const [from = '', to = ''] = range.split(/\s+to\s+/i).map((s) => s.trim());
  return {
    street: titleCaseStreet(street),
    from: titleCaseStreet(from),
    to: titleCaseStreet(to),
  };
}

/**
 * Codes the city uses as a "purpose" (job numbers like
 * `PMO307905-SWIT_BROK_REPA-10THAVE1SPANN`) are not readable text.
 */
export function isPublicWorksCode(value: string) {
  const text = value.trim();
  if (!text) return true;
  if (/[_]/.test(text)) return true;
  const tokens = text.split(/\s+/);
  if (tokens.length === 1 && /\d/.test(text) && text.length > 6) return true;
  if (tokens.length <= 2 && /\d{6,}/.test(text)) return true;
  const alnum = tokens.filter((token) => /\d/.test(token) && /[A-Za-z]/.test(token));
  return alnum.length > tokens.length / 2;
}

const TRAILING_ADDRESS =
  /\s+\d{1,5}[a-z]?\s+(?:\S+\s+){0,2}(?:st|street|ave|avenue|blvd|boulevard|way|dr|drive|rd|road|ln|lane|ct|court|pl|place|ter|terrace|hwy|highway|cir|circle|alley|aly)\b\.?(?:,?\s+san\s+francisco)?(?:,?\s+ca)?(?:\s+\d{5})?\.?\s*$/i;

/** Drops a trailing "1212 24TH AVE SAN FRANCISCO" from work text. */
function stripTrailingAddress(value: string) {
  return value.replace(TRAILING_ADDRESS, '').trim();
}

function looksLikeAddress(value: string) {
  return /^\d+[a-z]?\s+\S/i.test(value.trim());
}

/** Drops a shouted "APPROVAL FOR LEGISLATED PROGRAM:" label ahead of mixed-case text. */
function stripProgramLabel(value: string) {
  const match = /^([A-Z][A-Z\s/&-]{6,}):\s*([\s\S]*)$/.exec(value.trim());
  return match && /[a-z]/.test(match[2] ?? '') ? match[2]!.trim() : value;
}

/** Drops the "Address: 1850 ANZA ST" tail the city appends to some descriptions. */
function stripAddressTail(value: string) {
  return value.replace(/\s*[-–·,;]?\s*Address\s*:\s*.*$/i, '').trim();
}

/** "Street / Roadway:Sidewalk/Curb:Sewer" → "Street / Roadway, Sidewalk/Curb, Sewer". */
function readableList(value: string) {
  return value
    .split(/\s*:\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
}

export function publicWorksTypeKey(record: RecordSummary): PublicWorksTypeKey {
  const permit = field(record, 'Permit number') || record.id;
  for (const [pattern, key] of TYPE_BY_PREFIX) if (pattern.test(permit)) return key;
  const type = field(record, 'Permit type');
  for (const [pattern, key] of TYPE_BY_NAME) if (pattern.test(type)) return key;
  return 'other';
}

export function publicWorksPlacement(record: RecordSummary): PublicWorksPlacement {
  if (record.locationScope === 'address') return 'address';
  const publishedAddress = field(record, 'Published permit address');
  return publishedAddress && looksLikeAddress(publishedAddress) ? 'neighbor' : 'block';
}

export function publicWorksState(record: RecordSummary, now: number): PublicWorksState {
  if (isClosed(record.status)) return 'closed';
  const start = documentTime(field(record, 'Permit window start'));
  const end = documentTime(field(record, 'Permit window end'));
  if (end !== null && end + DAY <= now) return 'ended';
  if (start !== null && start > now) return 'upcoming';
  if (start !== null && end !== null) return 'active';
  return 'open';
}

/** "APPROVAL FOR LEGISLATED PROGRAM: Shared Spaces Fixed Commercial Parklet occupying…" → "Shared Spaces parklet". */
function parkletHeadline(text: string) {
  return /shared\s+spaces/i.test(text) ? 'Shared Spaces parklet' : 'Parklet';
}

/** Which dates a permit carries, so copy says "work window" only for actual work. */
export function publicWorksDateKind(typeKey: PublicWorksTypeKey) {
  return OCCUPANCY_TYPES.has(typeKey) ? 'period' : 'window';
}

/**
 * The permit dates as one i18n key under `parcelWatch.publicWorks`, with the
 * timestamps the caller formats: `window`/`period` (start – end),
 * `windowDay`/`periodDay` (same day), `…From`, `…Until`.
 */
export function publicWorksDates(details: {
  typeKey: PublicWorksTypeKey;
  start: number | null;
  end: number | null;
}) {
  const kind = publicWorksDateKind(details.typeKey);
  const { start, end } = details;
  if (start !== null && end !== null)
    return Math.abs(end - start) < DAY
      ? { key: `${kind}Day` as const, start, end }
      : { key: kind, start, end };
  if (start !== null) return { key: `${kind}From` as const, start, end };
  if (end !== null) return { key: `${kind}Until` as const, start, end };
  return null;
}

/** Everything the feed and record panel need to describe a street permit. */
export function publicWorksDetails(record: RecordSummary, now: number) {
  const permitNumber = field(record, 'Permit number') || record.id.split(':')[0]!;
  const publishedAddress = field(record, 'Published permit address');
  const block = publicWorksStreetBlock(field(record, 'Street block'));
  const placement = publicWorksPlacement(record);
  const workRaw = stripTrailingAddress(stripAddressTail(field(record, 'Work')));
  const work = !isPublicWorksCode(workRaw) && !looksLikeAddress(workRaw) ? cityText(workRaw) : '';
  const purposeRaw = stripTrailingAddress(
    stripProgramLabel(stripAddressTail(field(record, 'Purpose'))),
  );
  const purpose =
    !isPublicWorksCode(purposeRaw) && !looksLikeAddress(purposeRaw) ? cityText(purposeRaw) : '';
  const typeKey = publicWorksTypeKey(record);
  const typeRaw = field(record, 'Permit type');
  const texts = [work, purpose].filter(Boolean);
  const headline =
    texts.find((text) => text.length <= HEADLINE_MAX) ??
    (typeKey === 'parklet' ? parkletHeadline(texts[0] ?? '') : '');
  const detail = texts.find((text) => text !== headline) ?? '';
  const state = publicWorksState(record, now);
  const start = documentTime(field(record, 'Permit window start'));
  const end = documentTime(field(record, 'Permit window end'));
  const approved = documentTime(field(record, 'Approved'));
  const holder = cityText(field(record, 'Permit holder'));
  const utility = readableList(cityText(field(record, 'Utility type')));
  const daysUntilStart = start !== null && start > now ? Math.ceil((start - now) / DAY) : null;
  const dated = approved ?? documentTime(record.date);
  const staleOpen =
    state === 'open' &&
    start === null &&
    end === null &&
    dated !== null &&
    now - dated >= STALE_OPEN_DAYS * DAY;
  return {
    permitNumber,
    placement,
    publishedAddress: publishedAddress ? titleCaseStreet(publishedAddress) : '',
    block,
    typeKey,
    typeRaw,
    dateKind: publicWorksDateKind(typeKey),
    headline,
    detail,
    state,
    compact: state === 'ended' || state === 'closed' || staleOpen,
    attention: state === 'upcoming' || state === 'active',
    start,
    end,
    approved,
    daysUntilStart,
    holder,
    utility,
    status: record.status,
  };
}

export type PublicWorksDetails = ReturnType<typeof publicWorksDetails>;
